import { useState } from "react";
import { ChevronDown, Mail, Phone, Megaphone, Copy } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export const LEAD_STATUS = {
  nuovo: { label: "Nuovo", cls: "bg-cyan-500/10 text-cyan-300 border-cyan-500/25" },
  contattato: { label: "Contattato", cls: "bg-amber-500/10 text-amber-300 border-amber-500/25" },
  risposto: { label: "Ha risposto", cls: "bg-violet-500/10 text-violet-300 border-violet-500/25" },
  convertito: { label: "Convertito", cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/25" },
  perso: { label: "Perso", cls: "bg-white/[0.04] text-slate-400 border-white/10" },
};

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const HISTORY_LABEL = {
  created: "Lead ricevuto",
  duplicate: "Richiesta duplicata",
  status: "Cambio stato",
  follow_up_prepared: "Follow-up preparato",
  follow_up_sent: "Follow-up inviato",
  follow_up_failed: "Invio fallito",
  follow_up_rejected: "Follow-up rifiutato",
};

export function LeadRow({ lead, onStatus }) {
  const [open, setOpen] = useState(false);
  const st = LEAD_STATUS[lead.status] || LEAD_STATUS.nuovo;
  const src = lead.source || {};

  return (
    <div className="nadia-card overflow-hidden" data-testid={`lead-${lead.id}`}>
      <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <button onClick={() => setOpen((o) => !o)} className="flex-1 min-w-0 text-left flex items-start gap-3" data-testid={`lead-toggle-${lead.id}`}>
          <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-400/20 to-violet-500/20 border border-white/10 flex items-center justify-center text-sm font-heading font-bold text-slate-100 shrink-0">
            {(lead.name || "?").slice(0, 1).toUpperCase()}
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-slate-100">{lead.name}</span>
              <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border ${st.cls}`} data-testid={`lead-status-badge-${lead.id}`}>
                {st.label}
              </span>
              {lead.submissions > 1 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-white/[0.03] border-white/10 text-slate-400" data-testid={`lead-dup-${lead.id}`}>
                  <Copy size={9} /> {lead.submissions - 1} duplicat{lead.submissions - 1 === 1 ? "o" : "i"} evitat{lead.submissions - 1 === 1 ? "o" : "i"}
                </span>
              )}
            </div>
            <div className="mt-1 flex items-center gap-3 text-[11px] text-slate-500 flex-wrap">
              {lead.email && <span className="inline-flex items-center gap-1"><Mail size={10} />{lead.email}</span>}
              {lead.phone && <span className="inline-flex items-center gap-1 font-mono"><Phone size={10} />+{lead.phone}</span>}
              <span className="inline-flex items-center gap-1"><Megaphone size={10} />{src.campaign_name || "campagna"} · landing {src.variant || "A"}</span>
              <span>{fmt(lead.created_at)}</span>
            </div>
          </div>
          <ChevronDown size={14} className={`text-slate-500 mt-2 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        <Select value={lead.status} onValueChange={(v) => onStatus(lead.id, v)}>
          <SelectTrigger className="w-full sm:w-44 h-9 bg-[#0B1120] border-white/10 text-slate-100 text-xs" data-testid={`lead-status-select-${lead.id}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#131C2E] border-white/10 text-slate-100">
            {Object.entries(LEAD_STATUS).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {open && (
        <div className="border-t border-white/5 bg-white/[0.015] px-4 py-3" data-testid={`lead-history-${lead.id}`}>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Storico · memoria di Nadia</div>
          <ul className="space-y-1.5">
            {(lead.history || []).slice().reverse().map((h, i) => (
              <li key={i} className="flex items-start gap-2 text-xs">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-cyan-400/70 shrink-0" />
                <span className="text-slate-500 font-mono shrink-0">{fmt(h.at)}</span>
                <span className="text-slate-300"><span className="text-slate-100 font-semibold">{HISTORY_LABEL[h.kind] || h.kind}</span> — {h.note}{h.mock ? " (invio non effettuato)" : ""}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
